import React, { useState, useEffect } from "react";
import { ArrowLeft, Loader } from "lucide-react";
import axios from "axios";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "utils/constant";
import PDFReport from "./PDFReport";
import { IoIosCheckmarkCircle } from "react-icons/io";
import PermissionEmptyState from "components/UI_components/EmptyStateFinance";
import { rtdb } from "utils/firebase";
import { environment } from "utils/constant";
import { date } from "zod";
import {
  MdLocalShipping,
  MdOutlineAccountBalanceWallet,
  MdOutlineTask,
} from "react-icons/md";
import { MdOutlineGroup } from "react-icons/md";
import DeliveryIcon from "assets/Deliveries.svg";
import SyncIcon from "assets/Change.svg";
import { getFinanceCategories } from "utils/getFinanceCategories";
const ReportGenerator = ({ userPermissions, permissionsLoading }) => {
  const navigate = useNavigate();
  const categoryData = getFinanceCategories();
  const refundCategory = categoryData.refundCategory;
  const receivedCategory = categoryData.receivedCategory;
  const [selectedSections, setSelectedSections] = useState({
    site_updates: true,
    payments: true,
    attendance: true,
    material_inventory: true,
    material_deliveries: true,
    tasks: true,
  });

  const [dateRange, setDateRange] = useState({
    start_date: new Date().toISOString().split("T")[0],
    end_date: new Date().toISOString().split("T")[0],
  });

  const [selectedTimePeriod, setSelectedTimePeriod] = useState("Today");

  const siteData = JSON.parse(localStorage.getItem("siteData") || "{}");
  const siteId = localStorage.getItem("site");

  const [exportFormat, setExportFormat] = useState("PDF");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [configChanged, setConfigChanged] = useState(false);

  const sections = [
    {
      id: "site_updates",
      label: "Site Updates",
      description: "See site updates and photos at your project",
      icon: (
        <img
          src={SyncIcon}
          alt="Site Updates"
          style={{ width: 24, height: 24 }}
        />
      ),
    },
    {
      id: "payments",
      label: "Payments & Transactions",
      description: "Manage payment & transaction for your report",
      icon: <MdOutlineAccountBalanceWallet size={28} color="#B50000B0" />,
    },
    {
      id: "attendance",
      label: "Attendance",
      description: "View attendance of your report across project",
      icon: <MdOutlineGroup size={24} color="#CA7900AB" />,
    },
    {
      id: "material_inventory",
      label: "Material Inventory",
      description: "See what materials you have in your inventory",
      icon: <MdLocalShipping size={24} color="#6A02BC8A" />,
    },
    {
      id: "material_deliveries",
      label: "Material Deliveries",
      description: "Manage deliveries in your material report",
      icon: (
        <img
          src={DeliveryIcon}
          alt="Material Deliveries"
          style={{ width: 24, height: 24 }}
        />
      ),
    },
    {
      id: "tasks",
      label: "Tasks",
      description: "View and manage tasks for your project",
      icon: <MdOutlineTask size={24} color="#0094FFC2" />,
    },
  ];

  useEffect(() => {
    if (configChanged) {
      setReportData(null);
      setIsDownloading(false);
      setConfigChanged(false);
    }
  }, [configChanged]);

  const handleDateChange = (field, value) => {
    const today = new Date().toISOString().split("T")[0];
    if (value > today) return;

    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
    setSelectedTimePeriod("Specific");
    setConfigChanged(true);
  };

  const handleTimePeriodChange = (period) => {
    setSelectedTimePeriod(period);
    const today = new Date();
    let startDate, endDate;

    switch (period) {
      case "Today":
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case "7 Days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        endDate = new Date(today);
        break;
      case "1 Month":
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 1);
        endDate = new Date(today);
        break;
      case "Specific":
        // Don't change dates for specific - let user manually select
        return;
      default:
        return;
    }

    setDateRange({
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0],
    });
    setConfigChanged(true);
  };

  const getImageUrl = async (mediaId) => {
    if (!mediaId) return null;
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(`${BASE_URL}/core/api/snag/${mediaId}/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        return data.data.image;
      }
      return null;
    } catch (error) {
      console.error("Error fetching media URL:", error);
      return null;
    }
  };

  const getDateTimestamp = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return date.getTime();
    } catch (error) {
      return null;
    }
  };

  const normalizeTimestamp = (timestamp) => {
    if (!timestamp) return null;
    if (typeof timestamp === "number") return timestamp;
    if (typeof timestamp === "string") {
      const parsed = new Date(timestamp).getTime();
      return isNaN(parsed) ? null : parsed;
    }
    if (timestamp instanceof Date) return timestamp.getTime();
    return null;
  };

  const fetchSiteUpdates = async () => {
    if (
      !selectedSections.site_updates ||
      !siteId ||
      !dateRange.start_date ||
      !dateRange.end_date
    ) {
      return {};
    }

    const generalMessagesRef = rtdb.ref(
      `site_snags-${environment}/${siteId}/general_updates/messages`
    );

    const startTimestamp = getDateTimestamp(dateRange.start_date);
    const endTimestamp = getDateTimestamp(dateRange.end_date);
    if (!startTimestamp || !endTimestamp) return {};

    const endOfDayTimestamp = endTimestamp + 24 * 60 * 60 * 1000 - 1;

    try {
      const snapshot = await generalMessagesRef.once("value");
      if (!snapshot.exists()) return {};

      const allMessages = [];
      const newImageUrls = {};

      snapshot.forEach((childSnapshot) => {
        const messageData = childSnapshot.val();
        if (!messageData) return;

        const messageTimestamp = normalizeTimestamp(messageData.createdAt);
        if (!messageTimestamp) return;

        if (
          messageTimestamp >= startTimestamp &&
          messageTimestamp <= endOfDayTimestamp
        ) {
          allMessages.push({
            id: childSnapshot.key,
            ...messageData,
            createdAt: messageTimestamp,
          });
        }
      });

      const imagePromises = allMessages
        .filter((msg) => msg.has_media)
        .map(async (msg) => {
          if (
            !Array.isArray(msg.media) &&
            msg.media.type === "image" &&
            !newImageUrls[msg.media.mediaId]
          ) {
            const url = await getImageUrl(msg.media.mediaId);
            console.log("Old Image URL:", msg.media.mediaId, url);
            if (url) newImageUrls[msg.media.mediaId] = url;
          } else if (Array.isArray(msg.media)) {
            for (const media of msg.media) {
              if (!newImageUrls[media.mediaId] && media.type === "image") {
                const url = await getImageUrl(media.mediaId);
                console.log("New Image URL:", media.mediaId, url);
                if (url) newImageUrls[media.mediaId] = url;
              }
            }
          }
        });

      await Promise.all(imagePromises);

      const groupedUpdates = allMessages.reduce((acc, msg) => {
        const date = new Date(msg.createdAt).toLocaleDateString();
        if (!acc[date]) acc[date] = [];
        acc[date].push({
          comment: msg.text || "No comment",
          media:
            Array.isArray(msg.media) && msg.has_media
              ? msg.media.map((media) => ({
                  mediaId: media.mediaId,
                  url:
                    newImageUrls[media.mediaId] ||
                    "https://cdn-icons-png.flaticon.com/512/13434/13434972.png",
                  // caption:only time in hh:mm:ss format
                  caption: new Date(msg.createdAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                }))
              : msg.has_media &&
                !Array.isArray(msg.media) &&
                msg.media.type === "image"
              ? [
                  {
                    mediaId: msg.media.mediaId,
                    url:
                      newImageUrls[msg.media.mediaId] ||
                      "https://cdn-icons-png.flaticon.com/512/13434/13434972.png",
                    caption: new Date(msg.createdAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    }),
                  },
                ]
              : [],

          createdAt: msg.createdAt,
          senderName: msg.senderName || "Unknown",
        });
        return acc;
      }, {});

      Object.keys(groupedUpdates).forEach((date) => {
        groupedUpdates[date].sort((a, b) => a.createdAt - b.createdAt);
      });

      return groupedUpdates;
    } catch (error) {
      console.error("Error fetching site updates:", error);
      return {};
    }
  };

  const fetchReportData = async () => {
    setIsLoading(true);
    setError(null);
    console.log("Fetching report data with sections:", dateRange);

    try {
      const sites = localStorage.getItem("site");
      const token = localStorage.getItem("access");

      const [apiResponse, siteUpdates] = await Promise.all([
        axios.post(
          `${BASE_URL}/task/generate-report/`,
          {
            sites: parseInt(sites),
            ...selectedSections,
            ...dateRange,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        ),
        fetchSiteUpdates(),
      ]);

      const combinedData = {
        ...apiResponse.data,
        data: {
          ...apiResponse.data.data,
          site_updates: siteUpdates,
        },
      };
      console.log("Combined Report Data:", combinedData);
      setReportData(combinedData);
      return combinedData;
    } catch (err) {
      setError("Failed to generate report. Please try again.");
      console.error("Report generation error:", err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const generateExcel = async (data) => {
    try {
      setIsDownloading(true);

      const wb = XLSX.utils.book_new();
      const siteInfoSheet = [
        {
          "Site Name": siteData.site_name,
          Address: siteData.address,
          City: siteData.city,
          State: siteData.state,
          Pincode: siteData.pincode,
        },
      ];
      const wsInfo = XLSX.utils.json_to_sheet(siteInfoSheet);
      XLSX.utils.book_append_sheet(wb, wsInfo, "Site Information");

      Object.entries(selectedSections).forEach(([section, isSelected]) => {
        if (isSelected && data[section]) {
          const formattedData = formatDataForExcel(data[section], section);
          const ws = XLSX.utils.json_to_sheet(formattedData);
          const maxWidth = 50;
          const columnWidths = {};
          formattedData.forEach((row) => {
            Object.keys(row).forEach((key) => {
              const length = String(row[key]).length;
              columnWidths[key] = Math.min(
                maxWidth,
                Math.max(columnWidths[key] || 0, length)
              );
            });
          });
          ws["!cols"] = Object.keys(columnWidths).map((key) => ({
            wch: columnWidths[key],
          }));
          XLSX.utils.book_append_sheet(
            wb,
            ws,
            section.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
          );
        }
      });

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `project_report_${dateRange.start_date}.xlsx`);
    } catch (err) {
      setError("Failed to generate Excel file. Please try again.");
      console.error("Excel generation error:", err);
    } finally {
      setTimeout(() => setIsDownloading(false), 2000);
    }
  };

  const handleGenerateReport = async () => {
    const data = await fetchReportData();
    if (!data) return;

    if (exportFormat === "Excel") {
      await generateExcel(data.data);
    }
  };

  const SectionCard = ({ section }) => (
    <div
      onClick={() => {
        setSelectedSections((prev) => ({
          ...prev,
          [section.id]: !prev[section.id],
        }));
        setConfigChanged(true);
      }}
      className="bg-white shadow-sm hover:shadow-md transition-shadow rounded-lg w-80 cursor-pointer"
    >
      <div className="p-4">
        <div className="flex justify-between items-start">
          <div className="space-y-2 w-[90%]">
            <div className="flex items-center gap-2">
              <span className="text-xl">{section.icon}</span>
              <h4 className="font-medium text-gray-800">{section.label}</h4>
            </div>
            <p className="text-sm text-gray-500 pr-4">{section.description}</p>
          </div>
          {selectedSections[section.id] && (
            <IoIosCheckmarkCircle className="h-5 w-5 text-text-clr" />
          )}
        </div>
      </div>
    </div>
  );

  const formatDataForExcel = (data, section) => {
    switch (section) {
      case "site_updates":
        return Object.entries(data).flatMap(([date, updates]) =>
          updates.map((update) => ({
            Date: date,
            Time: new Date(update.createdAt).toLocaleTimeString(),
            Comment: update.comment,
            "Posted By": update.senderName,
            "Has Photo": update.mediaUrl ? "Yes" : "No",
          }))
        );

      case "payments":
        return data.map((payment) => ({
          Amount: `₹${parseFloat(payment.amount).toFixed(2)}`,
          Date: payment.transaction_date,
          Method: payment.transaction_method,
          Type: payment.transaction_type || payment?.payment_type || "N/A",
          Comments: payment.comments || "",
          Purpose:
            payment.transaction_catagory_id === refundCategory ||
            payment.transaction_catagory_id === receivedCategory
              ? "Credit"
              : "Debit",
        }));

      case "attendance":
        const attendanceRows = [];
        Object.entries(data).forEach(([date, attendance]) => {
          if (attendance.total_present > 0) {
            attendanceRows.push({
              Date: date,
              "Total Present": attendance.total_present,
              "Worker Present": attendance.worker_present,
              "Worker Absent": attendance.worker_absent,
              "Worker Name": "",
              "Work Type": "",
              Attendance: "",
            });
            attendance.data.forEach((record) => {
              attendanceRows.push({
                Date: date,
                "Total Present": "",
                "Worker Present": "",
                "Worker Absent": "",
                "Worker Name": record.worker_name,
                "Work Type": record.worker_workname,
                Attendance: record.attendance,
              });
            });
          }
        });
        return attendanceRows;

      case "material_inventory":
        const inventoryRows = [];
        Object.entries(data).forEach(([category, items]) => {
          inventoryRows.push({
            Category: category,
            "Current Quantity": "",
            "Update Date": "",
            "Entered Quantity": "",
            Comment: "",
          });
          items.forEach((item) => {
            inventoryRows.push({
              Category: category,
              "Current Quantity": item.current_quantity,
              "Update Date": item.update_date,
              "Entered Quantity": item.entered_quantity,
              Comment: item.comment,
            });
          });
        });
        return inventoryRows;

      case "tasks":
        const taskRows = [];
        data.forEach((task) => {
          taskRows.push({
            "Task Name": task.task_name,
            Status: task.task_status,
            Details: task.task_details,
            Priority: task.priority,
            Timeline: `${task.start_date} to ${task.end_date}`,
            "Estimated Amount": `₹${parseFloat(task.estimated_amount).toFixed(
              2
            )}`,
            Type: "Main Task",
            "Subtask Name": "",
            Material: "",
            Quantity: "",
            UOM: "",
          });
          if (task.subtasks?.length > 0) {
            task.subtasks.forEach((subtask) => {
              taskRows.push({
                "Task Name": task.task_name,
                Status: subtask.is_completed ? "Completed" : "Pending",
                Details: "",
                Priority: "",
                Timeline: `${subtask.start_date} to ${subtask.end_date}`,
                "Estimated Amount": "",
                Type: "Subtask",
                "Subtask Name": subtask.subtask_name,
                Material: "",
                Quantity: "",
                UOM: "",
              });
            });
          }
          if (task.task_materials?.length > 0) {
            task.task_materials.forEach((material) => {
              taskRows.push({
                "Task Name": task.task_name,
                Status: "",
                Details: "",
                Priority: "",
                Timeline: "",
                "Estimated Amount": "",
                Type: "Material",
                "Subtask Name": "",
                Material: material.material_name,
                Quantity: `${material.estimated_quantity}/${material.total_used}`,
                UOM: material.uom,
              });
            });
          }
        });
        return taskRows;

      default:
        return data;
    }
  };

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="w-10 h-10 animate-spin text-purple-500" />
      </div>
    );
  }

  if (
    !userPermissions.is_admin &&
    !userPermissions.report_generation &&
    !permissionsLoading
  ) {
    return (
      <PermissionEmptyState
        module="Report Generation"
        description="You don't have permission to access the report generation for this site. Only the administrator can access this feature."
      />
    );
  }

  const handleExportFormatChange = (format) => {
    setExportFormat(format);
    setReportData(null);
    setIsDownloading(false);
  };

  const handlePDFDownload = (e) => {
    if (isDownloading) {
      e.preventDefault();
      return false;
    }
    setIsDownloading(true);
    setTimeout(() => setIsDownloading(false), 3000);
  };

  return (
    <div className="h-[90vh] w-full bg-gray-50 p-6 overflow-y-auto">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="bg-white shadow-sm rounded-lg p-4 flex justify-between items-center">
          <ArrowLeft
            onClick={() => navigate(-1)}
            className="text-gray-600 cursor-pointer"
            size={20}
          />
          <h2 className="text-lg font-semibold">Generate Report</h2>
          <div style={{ width: 20, height: 20 }} />
        </div>

        <div className="bg-white shadow-sm rounded-lg p-6 space-y-4">
          <h3 className="text-sm font-medium">Select Time Period</h3>
          <div className="flex space-x-3 mb-6">
            {["Today", "7 Days", "1 Month", "Specific"].map((period) => (
              <button
                key={period}
                onClick={() => handleTimePeriodChange(period)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedTimePeriod === period
                    ? "bg-purple-100 text-purple-700 border border-purple-200"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {period}
              </button>
            ))}
          </div>

          <h3 className="text-sm font-medium">Select Date Range</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">From</label>
              <input
                type="date"
                value={dateRange.start_date}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => handleDateChange("start_date", e.target.value)}
                className="w-full p-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">To</label>
              <input
                type="date"
                value={dateRange.end_date}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => handleDateChange("end_date", e.target.value)}
                className="w-full p-2 border rounded-lg"
              />
            </div>
          </div>

          <h3 className="text-sm font-medium mb-2">Export Format</h3>
          <div className="flex space-x-4">
            <button
              onClick={() => handleExportFormatChange("PDF")}
              className={`px-4 py-1  rounded-full ${
                exportFormat === "PDF"
                  ? "bg-purple-100 text-purple-700"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              PDF
            </button>
            <button
              onClick={() => handleExportFormatChange("Excel")}
              className={`px-4 py-1  rounded-full ${
                exportFormat === "Excel"
                  ? "bg-purple-100 text-purple-700"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              Excel
            </button>
          </div>
        </div>

        {/* <div className="bg-white shadow-sm rounded-lg p-6"></div> */}

        <div className="bg-white shadow-sm rounded-lg p-4 items-center flex flex-col">
          <h3 className="text-lg font-semibold text-center mb-6">
            Report Sections
          </h3>
          <div
            className="flex flex-row flex-wrap justify-center w-[70vw]"
            style={{ rowGap: "1.5rem", gap: "40px" }}
          >
            {sections.map((section) => (
              <SectionCard key={section.id} section={section} />
            ))}
          </div>
        </div>

        <div className="flex justify-center">
          {exportFormat === "PDF" && reportData ? (
            <div className="relative">
              {isDownloading && (
                <div className="absolute inset-0 bg-gray-400 text-gray-600 px-8 py-3 rounded-lg font-semibold cursor-not-allowed flex items-center justify-center z-10">
                  Download Started...
                </div>
              )}
              <PDFDownloadLink
                document={
                  <PDFReport
                    data={reportData}
                    dateRange={dateRange}
                    selectedSections={selectedSections}
                  />
                }
                fileName={`${siteData.site_name}_report_${Date.now()}.pdf`}
                className={`px-8 py-3 rounded-lg font-semibold transition-colors ${
                  isDownloading
                    ? "bg-gray-300 text-gray-500 pointer-events-none"
                    : "bg-[#3B0069] text-white hover:bg-purple-800"
                }`}
                onClick={handlePDFDownload}
              >
                {({ loading }) =>
                  loading ? "Loading document..." : "Download PDF"
                }
              </PDFDownloadLink>
            </div>
          ) : (
            <button
              onClick={handleGenerateReport}
              disabled={isLoading || isDownloading}
              className="bg-[#3B0069] text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading
                ? "Generating..."
                : isDownloading
                ? "Downloading..."
                : "Generate Report"}
            </button>
          )}
        </div>

        {error && <div className="text-red-500 text-center">{error}</div>}
      </div>
    </div>
  );
};

export default ReportGenerator;
